import { Link } from "react-router-dom";
import { useState } from "react";
import { Home as HomeIcon, Building2, UtensilsCrossed, Check, ArrowUpRight, Info } from "lucide-react";
import { Reveal } from "../components/Motion";

const residential = [
  { name: "Standard Clean", price: "$150 – $280", unit: "flat rate by home size", desc: "Regular upkeep — kitchens, bathrooms, floors, dusting and surfaces.", features: ["All rooms & common areas", "Kitchen & bathrooms", "Floors vacuumed & mopped", "Dusting & surface wipe-down"] },
  { name: "Deep Clean", price: "$300 – $450", unit: "flat rate by home size", desc: "Top-to-bottom detail clean — baseboards, inside appliances, grout and more.", features: ["Everything in Standard", "Inside oven & fridge", "Baseboards & window sills", "Grout & tile detailing"], featured: true },
  { name: "Move-In / Move-Out", price: "$250 – $450", unit: "flat rate by home size", desc: "Empty-home reset so you hand over — or move into — a spotless space.", features: ["Full deep clean", "Inside all cabinets", "Wall spot-cleaning", "Move-ready finish"] },
];

const commercial = [
  { name: "Small Office", price: "from $250", unit: "/ month", sub: "Under 1,000 sq ft", desc: "Recurring cleaning for compact workspaces, studios and clinics.", features: ["Flexible frequency", "Desks & common areas", "Washrooms sanitized", "Trash & recycling"] },
  { name: "Medium Office", price: "$500 – $2,000", unit: "/ month", sub: "1,000 – 5,000 sq ft", desc: "Scaled janitorial based on square footage and how often you need us.", features: ["Priced by sq ft & frequency", "Floors & carpets", "Kitchen / break rooms", "Nightly or weekly plans"], featured: true },
  { name: "Restaurant / Kitchen", price: "Custom Quote", unit: "after free walkthrough", desc: "Degreasing and back-of-house cleaning to health-code standard.", features: ["Kitchen degreasing", "Hood & equipment cleaning", "Dining & front-of-house", "Health-code compliant"] },
];

function PriceCard({ item, cta }) {
  return (
    <div data-testid={`price-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`relative h-full rounded-sm border p-8 flex flex-col transition-shadow hover:shadow-[0_25px_60px_-30px_rgba(4,27,36,0.4)] ${item.featured ? "border-tide bg-white ring-1 ring-tide" : "border-border bg-white"}`}>
      {item.featured && <span className="absolute -top-3 left-8 bg-tide text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-sm">Most Popular</span>}
      <h3 className="font-heading font-bold text-2xl text-ink">{item.name}</h3>
      {item.sub && <span className="text-sm text-tide font-semibold mt-1">{item.sub}</span>}
      <div className="mt-4 flex items-end gap-2">
        <span className="font-heading font-extrabold text-3xl tracking-tight text-ink">{item.price}</span>
        <span className="text-ink/50 text-sm mb-1">{item.unit}</span>
      </div>
      <p className="mt-3 text-ink/60 leading-relaxed">{item.desc}</p>
      <ul className="mt-6 space-y-3 flex-1">
        {item.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-ink/75">
            <Check size={18} className="text-tide mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <Link to="/book" className={`mt-8 inline-flex items-center justify-center gap-2 rounded-sm font-semibold px-6 py-3.5 transition-transform hover:-translate-y-0.5 ${item.featured ? "bg-ink text-white hover:bg-brand" : "border border-ink/20 text-ink hover:border-tide hover:text-tide"}`}>
        {cta} <ArrowUpRight size={16} />
      </Link>
    </div>
  );
}

export default function Services() {
  const [tab, setTab] = useState("residential");
  const data = tab === "residential" ? residential : commercial;

  return (
    <div>
      <section className="pt-32 md:pt-40 pb-14 bg-secondary grain relative">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">Services &amp; Pricing</span>
            <h1 className="mt-4 font-heading font-extrabold tracking-tighter text-5xl md:text-6xl text-ink max-w-3xl leading-[1.05]">
              Transparent pricing. No surprises.
            </h1>
            <p className="mt-5 text-ink/60 text-lg max-w-2xl">
              Clear starting ranges for every service. Recurring clients save 10–15% on bi-weekly and monthly plans.
            </p>
          </Reveal>

          <div className="mt-10 inline-flex rounded-sm border border-border bg-white p-1">
            {[["residential", HomeIcon, "Residential"], ["commercial", Building2, "Commercial"]].map(([key, Icon, label]) => (
              <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 rounded-sm px-6 py-3 text-sm font-semibold transition-colors ${tab === key ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {data.map((item, i) => (
            <Reveal key={item.name} delay={i * 0.08}>
              <PriceCard item={item} cta={tab === "commercial" && item.name.includes("Restaurant") ? "Request Walkthrough" : "Book This Service"} />
            </Reveal>
          ))}
        </div>

        {tab === "residential" && (
          <Reveal>
            <div className="mt-8 rounded-sm border border-tide/30 bg-tide/5 p-6 flex items-start gap-3">
              <Info size={20} className="text-tide mt-0.5 shrink-0" />
              <p className="text-ink/75"><strong className="text-ink">Recurring discount:</strong> Save 10–15% when you book bi-weekly or monthly recurring cleans.</p>
            </div>
          </Reveal>
        )}
        {tab === "commercial" && (
          <Reveal>
            <div className="mt-8 rounded-sm border border-tide/30 bg-tide/5 p-6 flex items-start gap-3">
              <Info size={20} className="text-tide mt-0.5 shrink-0" />
              <p className="text-ink/75"><strong className="text-ink">Restaurant & kitchen cleaning</strong> is custom-quoted after a free walkthrough due to degreasing and health-code requirements.</p>
            </div>
          </Reveal>
        )}

        <Reveal>
          <p className="mt-8 text-center text-ink/55 italic">Final price confirmed after a free walkthrough or quote form.</p>
        </Reveal>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Not sure which plan fits? Let's talk.</h2>
          <Link to="/book" className="inline-flex items-center gap-2 rounded-sm bg-tide text-white font-bold px-8 py-4 transition-transform hover:-translate-y-1">
            Get a Free Quote <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
